<script setup lang="ts">
import { computed } from 'vue'
import type { memberLuckyWheelDrawResponse } from '@/api/member'
import { getResultPopupViewModel, type LuckyWheelResultVariant } from '@/utils/raffle'

const props = withDefaults(
  defineProps<{
    show: boolean
    variant: LuckyWheelResultVariant
    result?: memberLuckyWheelDrawResponse | null
    freeChances?: number
    couponThreshold?: number
    /** 覆盖默认标题（如 WS 下发的 title） */
    customTitle?: string
    /** 覆盖默认高亮文案（如 WS 下发的 content） */
    customHighlightText?: string
  }>(),
  {
    result: null,
    freeChances: 1,
    couponThreshold: 500,
    customTitle: undefined,
    customHighlightText: undefined,
  },
)

const emit = defineEmits<{
  'update:show': [value: boolean]
  primary: []
  secondary: []
}>()

const viewModel = computed(() => {
  const base = getResultPopupViewModel(props.variant, {
    result: props.result,
    freeChances: props.freeChances,
    couponThreshold: props.couponThreshold,
  })

  if (props.customTitle) {
    base.title = props.customTitle
  }
  if (props.customHighlightText) {
    base.highlightText = props.customHighlightText
    base.highlightIsAmount = false
  }

  return base
})

const handlePrimary = () => emit('primary')
const handleSecondary = () => emit('secondary')
const highlightStyles = computed(() => {
  return {
    'text-sm font-din-bold leading-tight': true,
    'text-auxiliary-red font-din-bold text-[32px]': viewModel.value.highlightIsAmount,
    'text-text-primary text-base':
      !viewModel.value.highlightIsAmount && props.variant !== 'no_prize',
    'text-2xl text-text-secondary': props.variant === 'no_prize',
  }
})
</script>

<template>
  <van-popup
    :show="show"
    position="center"
    round
    :close-on-click-overlay="false"
    class="lucky-wheel-result-popup bg-transparent! overflow-visible!"
    @update:show="emit('update:show', $event)"
  >
    <div class="relative w-[320px] max-w-[86vw]">
      <img
        :src="viewModel.headerImage"
        alt=""
        class="absolute left-1/2 -top-12 -translate-x-1/2 w-full max-w-full pointer-events-none z-10"
        aria-hidden="true"
      />

      <div
        class="relative rounded-3xl bg-white px-5 pt-14 pb-5 shadow-[0_8px_32px_rgba(0,0,0,0.12)] overflow-hidden"
      >
        <div
          class="absolute inset-x-0 top-0 h-24 bg-linear-to-b from-[#FFE770] to-white pointer-events-none"
          aria-hidden="true"
        />

        <div class="relative z-10 text-center">
          <h3 class="text-xl font-bold text-[#662600] mb-3">{{ viewModel.title }}</h3>

          <div v-if="viewModel.highlightText" class="rounded-xl bg-[#FFF5D9] px-4 py-3 mb-3">
            <p class="" :class="highlightStyles" v-html="viewModel.highlightText"></p>
          </div>

          <p v-if="viewModel.description" class="text-xs text-text-third leading-relaxed mb-5 px-1">
            {{ viewModel.description }}
          </p>

          <div class="space-y-2.5">
            <button
              type="button"
              class="w-full h-11 rounded-full text-white! text-base font-semibold bg-linear-to-b from-[#FF6B90] to-[#EA0C00] active:opacity-90"
              @click="handlePrimary"
            >
              {{ viewModel.primaryText }}
            </button>
            <button
              v-if="viewModel.secondaryText"
              type="button"
              class="w-full h-11 rounded-full text-auxiliary-red! text-base font-semibold bg-white border border-auxiliary-red active:opacity-90"
              @click="handleSecondary"
            >
              {{ viewModel.secondaryText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
.lucky-wheel-result-popup :deep(.van-popup) {
  background: transparent;
  overflow: visible;
}
</style>
